import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

const WalletKudaPage = () => {
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [balance, setBalance] = useState(0);
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🎨 ORIGINAL THEME (card-first layout, different from #1 and #2)
  const theme = isGhana
    ? {
        bgColor: "bg-gradient-to-br from-[#0a0a1f] via-black to-[#0a0a1f]",
        cardGradient: "from-indigo-700 via-blue-600 to-cyan-500", // Ghana wallet 3 — cool blue
        accentColor: "text-cyan-300",
        cardBg: "bg-[#0a0f20]",
        cardBorder: "border-cyan-500/15",
        primaryBtn: "bg-cyan-500 hover:bg-cyan-600",
        cardChip: "from-yellow-300 to-yellow-500",
        currencySymbol: config.currencySymbol,
        walletBrand: "AT Money Card",
        walletTagline: "Mobile · Secure · Fast",
        cardName: "GHANA CITIZEN",
        actions: [
          { label: "Top Up", icon: "💰", route: "/purchase" },
          { label: "Send", icon: "📤", route: "/transfer-select", from: "kuda" },
          { label: "Bank", icon: "🏛️", route: "/transfer-select", from: "kuda" },
          { label: "Bills", icon: "📄", route: "/services/electricity" },
        ],
        flag: "🇬🇭",
      }
    : {
        bgColor: "bg-gradient-to-br from-[#0d0a1f] via-black to-[#0d0a1f]",
        cardGradient: "from-purple-700 via-fuchsia-600 to-violet-500", // Nigeria wallet 3 — purple
        accentColor: "text-purple-300",
        cardBg: "bg-[#0f0a20]",
        cardBorder: "border-purple-500/15",
        primaryBtn: "bg-purple-600 hover:bg-purple-700",
        cardChip: "from-yellow-300 to-yellow-500",
        currencySymbol: config.currencySymbol,
        walletBrand: "ProBank Card",
        walletTagline: "Simple. Free. Smart.",
        cardName: "NIGERIAN CITIZEN",
        actions: [
          { label: "Top Up", icon: "💰", route: "/purchase" },
          { label: "Send", icon: "📤", route: "/transfer-select", from: "kuda" },
          { label: "Bank", icon: "🏛️", route: "/transfer-select", from: "kuda" },
          { label: "Bills", icon: "📄", route: "/services/electricity" },
        ],
        flag: "🇳🇬",
      };

  const fetchKudaData = async () => {
    try {
      const res = await API.get("/dashboard");
      setBalance(Number(res.data?.balances?.account || res.data?.user?.balance || 0));
      setUser(res.data?.user ?? null);

      const txRes = await API.get("/transactions?wallet=kuda&limit=5"); // GET /api/transactions now exists
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
    } catch (err) {
      console.error("Error loading wallet data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKudaData();
    const interval = setInterval(fetchKudaData, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className={`min-h-screen ${theme.bgColor} flex items-center justify-center text-white`}>
        <p className="animate-pulse">Loading wallet...</p>
      </div>
    );
  }

  // Card number — masked, using user ID/email hash for consistency
  const last4 = (user?._id || user?.id || "0000").slice(-4);

  return (
    <div className={`min-h-screen ${theme.bgColor} text-white pb-20`}>

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* Compact Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-widest">{theme.walletTagline}</p>
          <h1 className="text-base font-bold">Hello, {user?.name || "User"}</h1>
        </div>
        <div className="flex gap-2">
          <button className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-xs hover:bg-white/10">?</button>
          <button className="w-8 h-8 bg-white/5 rounded-full flex items-center justify-center text-xs hover:bg-white/10">🔔</button>
        </div>
      </div>

      {/* ✨ Virtual Card Display (card-first layout — the hero of this page) */}
      <div className="px-5">
        <motion.div
          initial={{ opacity: 0, rotateY: -15, y: 20 }}
          animate={{ opacity: 1, rotateY: 0, y: 0 }}
          transition={{ duration: 0.6 }}
          className={`relative rounded-3xl p-6 bg-gradient-to-br ${theme.cardGradient} shadow-2xl overflow-hidden aspect-[16/10]`}
        >
          {/* Card decorative effects */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.15),_transparent_50%)]" />
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/10 rounded-full blur-3xl" />

          {/* Top row: Brand + Chip */}
          <div className="relative flex justify-between items-start">
            <div>
              <p className="text-xs opacity-80 uppercase tracking-wider">{theme.walletBrand}</p>
              <p className="text-[10px] opacity-70 mt-0.5">{theme.flag} {config.name}</p>
            </div>
            <div className={`w-10 h-7 rounded bg-gradient-to-br ${theme.cardChip} shadow-md`} />
          </div>

          {/* Middle: Balance */}
          <div className="relative mt-6">
            <p className="text-xs opacity-80">Available Balance</p>
            <p className="text-4xl font-extrabold tracking-tight mt-1">
              {theme.currencySymbol}{balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          {/* Bottom: Card number + name */}
          <div className="relative mt-4 flex justify-between items-end">
            <div>
              <p className="text-xs opacity-70 tracking-widest">•••• •••• •••• {last4}</p>
              <p className="text-xs font-semibold mt-1 tracking-wider uppercase">{user?.name || theme.cardName}</p>
            </div>
            <div className="text-xs opacity-90 italic font-serif">YourBank</div>
          </div>
        </motion.div>
      </div>

      {/* Quick Actions - 4 horizontal compact buttons.
          Send / Bank open the branded selector with Kuda's identity. */}
      <div className="grid grid-cols-4 gap-3 px-5 mt-5">
        {theme.actions.map((item, idx) => (
          <motion.button
            key={idx}
            whileTap={{ scale: 0.93 }}
            onClick={() =>
              item.from
                ? navigate(item.route, { state: { from: item.from } })
                : navigate(item.route)
            }
            className={`${theme.cardBg} rounded-2xl py-4 flex flex-col items-center gap-2 border ${theme.cardBorder} hover:brightness-125 transition`}
          >
            <div className="text-2xl">{item.icon}</div>
            <p className="text-xs font-medium">{item.label}</p>
          </motion.button>
        ))}
      </div>

      {/* Quick Stats Row (3 mini cards) */}
      <div className="px-5 mt-5 grid grid-cols-3 gap-3">
        <div className={`${theme.cardBg} rounded-xl p-3 border ${theme.cardBorder}`}>
          <p className="text-[10px] text-gray-400 uppercase tracking-widest">Sent</p>
          <p className="text-sm font-bold mt-1">{theme.currencySymbol}0</p>
          <p className="text-[10px] text-gray-500 mt-0.5">This month</p>
        </div>
        <div className={`${theme.cardBg} rounded-xl p-3 border ${theme.cardBorder}`}>
          <p className="text-[10px] text-gray-400 uppercase tracking-widest">Received</p>
          <p className="text-sm font-bold mt-1 text-green-400">{theme.currencySymbol}0</p>
          <p className="text-[10px] text-gray-500 mt-0.5">This month</p>
        </div>
        <div className={`${theme.cardBg} rounded-xl p-3 border ${theme.cardBorder}`}>
          <p className="text-[10px] text-gray-400 uppercase tracking-widest">Saved</p>
          <p className={`text-sm font-bold mt-1 ${theme.accentColor}`}>{theme.currencySymbol}0</p>
          <p className="text-[10px] text-gray-500 mt-0.5">All time</p>
        </div>
      </div>

      {/* Spending Chart Preview (visual placeholder) */}
      <div className={`mx-5 mt-5 ${theme.cardBg} rounded-2xl p-4 border ${theme.cardBorder}`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-semibold">Spending Insights</p>
            <p className="text-xs text-gray-400">Last 7 days</p>
          </div>
          <button className={`text-xs ${theme.accentColor}`}>View All ›</button>
        </div>
        {/* Bar chart visualization (CSS only) */}
        <div className="flex items-end justify-between gap-2 h-20 px-1">
          {[40, 65, 30, 80, 45, 70, 55].map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div
                className={`w-full rounded-t bg-gradient-to-t ${theme.cardGradient} opacity-70`}
                style={{ height: `${h}%` }}
              />
              <p className="text-[9px] text-gray-500">{["M", "T", "W", "T", "F", "S", "S"][i]}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className={`mx-5 mt-5 ${theme.cardBg} rounded-2xl p-4 border ${theme.cardBorder}`}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">Recent Activity</p>
          <button className="text-xs text-gray-400">See All</button>
        </div>

        {transactions.length > 0 ? (
          transactions.map((tx, idx) => (
            <div key={idx} className="flex justify-between items-center py-2 border-b border-white/5 last:border-0">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-sm`}>
                  {tx?.type === "credit" ? "↓" : "↑"}
                </div>
                <div>
                  <p className="font-medium text-sm">{tx?.title || "Transaction"}</p>
                  <p className="text-[10px] text-gray-400">
                    {tx?.createdAt ? new Date(tx.createdAt).toLocaleString() : ""}
                  </p>
                </div>
              </div>
              <p
                className={`font-semibold text-sm ${
                  tx?.status === "failed"
                    ? "text-red-400"
                    : tx?.status === "confirmed"
                    ? "text-green-400"
                    : "text-yellow-400"
                }`}
              >
                {theme.currencySymbol}{Number(tx?.amount ?? 0).toLocaleString()}
              </p>
            </div>
          ))
        ) : (
          <p className="text-center text-gray-500 text-sm py-4">No recent activity</p>
        )}
      </div>
    </div>
  );
};

export default WalletKudaPage;
